import React, { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { getProjectById } from '../data/projects';
import {
    MacWindow,
    CRTScreen,
    XRHeadset,
    DialogBox,
    Floppy,
    PixelCursor,
} from './motifs';
import SEO from './SEO';
import '../styles/v3-projects.css';

/**
 * Convert a demo URL into an embeddable iframe URL.
 * Returns null when the URL is not a known embeddable host.
 */
function toEmbedUrl(url) {
    if (!url) return null;
    if (url.includes('youtu.be/') || url.includes('youtube.com/watch')) {
        return (
            url
                .replace('youtu.be/', 'youtube.com/embed/')
                .replace('watch?v=', 'embed/') + '?autoplay=1&mute=1'
        );
    }
    if (url.includes('drive.google.com')) {
        return url.replace('/view', '/preview');
    }
    if (url.includes('linkedin.com/embed')) {
        return url;
    }
    return null;
}

function ProjectPage() {
    const { id } = useParams();
    const project = getProjectById(id);

    // Framer-motion setup for motif tilt
    const motifRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: motifRef, offset: ['start end', 'end start'] });
    const motifRotate = useTransform(scrollYProgress, [0, 1], [-2, 4]);

    if (!project) {
        return (
            <>
                <SEO
                    title="Project not found · Vijay Kagupati"
                    description="The project you're looking for doesn't exist."
                    noindex
                />
                <div className="v3-project">
                    <div>
                        <h1>Project not found</h1>
                        <Link to="/">Back to Home</Link>
                    </div>
                </div>
            </>
        );
    }

    const embedUrl = toEmbedUrl(project.liveDemoLink);
    const firstParagraph = project.fullDescription.split('\n\n')[0];
    const statusColor = project.blockColor;
    const year = new Date().getFullYear();

    return (
        <>
            <SEO
                title={`${project.title} · Vijay Kagupati`}
                description={project.shortDescription}
                image={project.image}
                url={`https://vijaypages.com/project/${project.id}`}
            />
            <div className="v3-project">
                {/* LEFT META COLUMN */}
                <motion.div
                    className="v3-project__meta"
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div className="v3-project__crumb">WORK · {year} · {project.tags[0]?.toUpperCase() || 'PROJECT'}</div>
                    <h1 className="v3-project__title">{project.title}</h1>
                    <p className="v3-project__lede">{firstParagraph}</p>

                    {/* SPECS GRID */}
                    <div className="v3-project__specs">
                        <div className="v3-project__spec-item">
                            <div className="v3-project__spec-label">Role</div>
                            <div className="v3-project__spec-value">{project.role}</div>
                        </div>
                        <div className="v3-project__spec-item">
                            <div className="v3-project__spec-label">Stack</div>
                            <div className="v3-project__spec-value">
                                {project.technologies.slice(0, 2).join(' · ')}
                            </div>
                        </div>
                        <div className="v3-project__spec-item">
                            <div className="v3-project__spec-label">Device</div>
                            <div className="v3-project__spec-value">{project.tags[0]?.toUpperCase() || 'VR'}</div>
                        </div>
                        <div className="v3-project__spec-item">
                            <div className="v3-project__spec-label">Window</div>
                            <div className="v3-project__spec-value">{project.duration}</div>
                        </div>
                        <div className="v3-project__spec-item">
                            <div className="v3-project__spec-label">Team</div>
                            <div className="v3-project__spec-value">
                                {project.team ? project.team.length : '1'}
                            </div>
                        </div>
                        <div className="v3-project__spec-item">
                            <div className="v3-project__spec-label">Status</div>
                            <div className={`v3-project__status v3-project__status--${statusColor}`}>
                                ▮ SHIPPED
                            </div>
                        </div>
                    </div>

                    {/* CTAs */}
                    <div className="v3-project__ctas">
                        <a href="#overview" className="v3-project__cta-primary">
                            → READ THE TEARDOWN
                        </a>
                        {project.liveDemoLink && (
                            <a
                                href={project.liveDemoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="v3-project__cta-ghost"
                            >
                                PLAY DEMO ↗
                            </a>
                        )}
                        {project.metaStoreLink && (
                            <a
                                href={project.metaStoreLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="v3-project__cta-ghost"
                            >
                                META STORE ↗
                            </a>
                        )}
                    </div>
                </motion.div>

                {/* RIGHT SHOT COLUMN */}
                <motion.div
                    className="v3-project__shot"
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    {/* HUD CORNERS */}
                    <div className="v3-project__hud">
                        <div className="v3-project__hud-corner v3-project__hud-tl">FRAME 014</div>
                        <div className="v3-project__hud-corner v3-project__hud-tr">REC ●</div>
                        <div className="v3-project__hud-corner v3-project__hud-bl">— V.K · {project.tags[0]?.toUpperCase() || 'UNITY'} · {year} —</div>
                        <div className="v3-project__hud-corner v3-project__hud-br">[ 60 FPS ]</div>
                    </div>

                    {/* MOTIF RENDERING */}
                    <motion.div
                        className="v3-project__motif"
                        ref={motifRef}
                        style={{ rotate: motifRotate }}
                    >
                        {project.motif === 'xr' && <XRHeadset />}
                        {project.motif === 'macwin' && (
                            <MacWindow title={`${project.title.replace(/\s+/g, '')}.unity`}>
                                Build ready to ship.
                            </MacWindow>
                        )}
                        {project.motif === 'crt' && <CRTScreen />}
                        {project.motif === 'dialog' && <DialogBox message="Build ready to ship." />}
                        {project.motif === 'floppy' && (
                            <Floppy label={`${project.id}_${project.tags[0]}.app`} />
                        )}
                        {project.motif === 'cursor' && <PixelCursor size={72} />}
                        {!project.motif || project.motif === 'hud' ? null : null}
                    </motion.div>
                </motion.div>
            </div>

            {/* MEDIA BLOCK */}
            {embedUrl ? (
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
                    <div className="v3-project__media" id="overview">
                        <div className="v3-project__media-video">
                            <iframe
                                src={embedUrl}
                                title={`${project.title} Demo`}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                </div>
            ) : project.image ? (
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
                    <div className="v3-project__media" id="overview">
                        <div className="v3-project__media-image">
                            <img src={project.image} alt={project.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                    </div>
                </div>
            ) : null}

            {/* CONTENT SECTIONS */}
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
                {project.team && project.team.length > 0 && (
                    <div className="v3-project__section">
                        <h2 className="v3-project__section-heading">Team</h2>
                        <ul className="v3-project__team">
                            {project.team.map((member) => (
                                <li key={member.name}>
                                    <a
                                        href={member.linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="v3-project__team-link"
                                    >
                                        {member.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="v3-project__section">
                    <h2 className="v3-project__section-heading">Overview</h2>
                    {project.fullDescription.split('\n\n').map((paragraph, index) => (
                        <p key={index} className="v3-project__section-description">
                            {paragraph}
                        </p>
                    ))}
                </div>

                <div className="v3-project__section">
                    <h2 className="v3-project__section-heading">Key Features</h2>
                    <ul className="v3-project__section-list">
                        {project.features.map((feature, index) => (
                            <li key={index} className="v3-project__section-item">
                                {feature}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="v3-project__section">
                    <h2 className="v3-project__section-heading">Challenges</h2>
                    <ul className="v3-project__section-list">
                        {project.challenges.map((challenge, index) => (
                            <li key={index} className="v3-project__section-item">
                                {challenge}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </>
    );
}

export default ProjectPage;
